import {
    ArticleContentKey,
    ArticlePartContent,
    ArticleQuestion,
    BlockProposal,
    getDefaultForContent,
    getDefaultForQuestion,
} from '@/model/Article';
import {isTrailingLegalBlock, normalizeLoose, plainHeadingText} from '@/service/analyzeFidelity';
import {getUid} from '@/utils/uid';

export type ProposalChoice = 'accept' | 'keep';

/**
 * Review with no proposals is a failed enhance. The review screen stays
 * only when there is something to accept or keep.
 */
export function hollowReviewFailure(article: {
    isAnalyze?: boolean | null;
    aiGenerateStatus?: string | null;
    blockProposals?: unknown;
} | null | undefined): {enhanced: 'pending'; aiGenerateStatus: 'failed'} | null {
    if (!article?.isAnalyze || article.aiGenerateStatus !== 'review') {
        return null;
    }
    const proposals = Array.isArray(article.blockProposals) ? article.blockProposals : [];
    if (proposals.length > 0) {
        return null;
    }
    return {enhanced: 'pending', aiGenerateStatus: 'failed'};
}

type ProposalArticle = {
    h1?: ArticlePartContent[];
    h2?: ArticlePartContent[];
    h3?: ArticlePartContent[];
    h4?: ArticlePartContent[];
    h5?: ArticlePartContent[];
    h6?: ArticlePartContent[];
    questions?: ArticleQuestion[];
    blockProposals?: BlockProposal[];
};

const HEADING_PARTS: ArticleContentKey[] = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

function sameText(left: unknown, right: unknown): boolean {
    return plainHeadingText(left) === plainHeadingText(right);
}

function insertBeforeLegal(blocks: ArticlePartContent[]): number {
    for (let index = blocks.length - 1; index >= 0; index--) {
        if (!plainHeadingText(blocks[index]?.h) && !plainHeadingText(blocks[index]?.p)) {
            continue;
        }
        return isTrailingLegalBlock(blocks[index]) ? index : blocks.length;
    }
    return blocks.length;
}

function cloneProposals(list: BlockProposal[] | null | undefined): BlockProposal[] {
    return (list || []).map((item) => ({...item}));
}

/**
 * Accept writes the block once. A second accept does not add another copy.
 * Keep leaves the scraped text in place.
 */
export function applyBlockProposalChoice<T extends ProposalArticle>(
    article: T,
    proposalId: string,
    action: ProposalChoice,
): {changed: boolean; article: T} {
    const proposals = cloneProposals(article.blockProposals);
    const index = proposals.findIndex((item) => item.id === proposalId);
    if (index < 0) {
        return {changed: false, article};
    }
    const proposal = proposals[index];
    const next: T = {...article, blockProposals: proposals};
    if (proposal.state === 'failed' && action === 'keep') {
        proposals[index] = {...proposal, state: 'kept'};
        return {changed: true, article: next};
    }
    if (proposal.state !== 'pending') {
        return {changed: false, article};
    }
    if (action === 'keep') {
        proposals[index] = {...proposal, state: 'kept'};
        return {changed: true, article: next};
    }
    if (proposal.part === 'question') {
        const questions = (article.questions || []).map((item) => ({...item}));
        if (proposal.kind === 'replace' && questions[proposal.blockIndex]) {
            questions[proposal.blockIndex] = {
                ...questions[proposal.blockIndex],
                question: proposal.proposedHeading || questions[proposal.blockIndex].question,
                answer: proposal.proposedBody,
            };
        } else if (proposal.kind === 'insert') {
            const already = questions.some((item) =>
                sameText(item.question, proposal.proposedHeading) && sameText(item.answer, proposal.proposedBody));
            if (!already) {
                const row = getDefaultForQuestion();
                row.question = proposal.proposedHeading;
                row.answer = proposal.proposedBody;
                row.uid = getUid();
                questions.push(row);
            }
        }
        next.questions = questions;
    } else if (HEADING_PARTS.indexOf(proposal.part) !== -1) {
        const part = proposal.part;
        const blocks = ((article[part] || []) as ArticlePartContent[]).map((item) => ({...item}));
        if (proposal.kind === 'replace' && blocks[proposal.blockIndex]) {
            const currentHeading = blocks[proposal.blockIndex].h;
            const nextHeading = plainHeadingText(proposal.proposedHeading);
            blocks[proposal.blockIndex] = {
                ...blocks[proposal.blockIndex],
                h: nextHeading && normalizeLoose(nextHeading) !== normalizeLoose(currentHeading)
                    ? nextHeading
                    : currentHeading,
                p: proposal.proposedBody,
            };
        } else if (proposal.kind === 'insert') {
            const already = blocks.some((item) =>
                sameText(item.h, proposal.proposedHeading) && sameText(item.p, proposal.proposedBody));
            if (!already) {
                const row = getDefaultForContent();
                row.h = proposal.proposedHeading;
                row.p = proposal.proposedBody;
                row.uid = getUid();
                const at = insertBeforeLegal(blocks);
                blocks.splice(at, 0, row);
            }
        }
        next[part] = blocks;
    }
    proposals[index] = {...proposal, state: 'accepted'};
    return {changed: true, article: next};
}

export function pendingBlockProposals(list: BlockProposal[] | null | undefined): BlockProposal[] {
    return (list || []).filter((item) => item.state === 'pending');
}

/**
 * Settled means every block was accepted or kept. A pending or failed block
 * stays in review. An empty list is not done.
 */
export function proposalsAreSettled(list: Array<{state?: string | null}> | null | undefined): boolean {
    const items = list || [];
    return items.length > 0 && items.every((item) => item.state === 'accepted' || item.state === 'kept');
}

export function settledGenerationStatus(
    list: Array<{state?: string | null}> | null | undefined,
): {enhanced: 'done'; aiGenerateStatus: 'done'} | null {
    if (!proposalsAreSettled(list)) {
        return null;
    }
    return {enhanced: 'done', aiGenerateStatus: 'done'};
}
